/**
 * Escalations — are alerts being answered, and what is waiting right now? A site's Alert settings
 * tab, and the same for one site on the cloud's page (the cloud's API proxies to the site).
 *
 *   summary            one plain paragraph: how many alerts, how many answered and how fast, how
 *                      many had to be passed on to more people, how many were never answered
 *   needs attention    what is waiting now — alerts still being passed on to more people; for
 *                      system problems, the open ones with Acknowledge / Resolve
 *   four numbers       alerts · answered · usual time to answer · never answered
 *   more detail        per day, per notification, who answered, per alert type (folded away)
 *
 * "Answered" = acknowledged, assigned, resolved or cancelled — any of them stops the escalation.
 * "9 in 10 within" is the 90th percentile; "usually" the average.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ArrowPathIcon, CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Callout, StatusPill } from '../common/Callout';
import { AASegmented } from './AlertActionsControls';
import ResolveReport, { type ResolveReportValue } from './ResolveReport';
import {
  SYSTEM_ALERT_KINDS,
  alertActionsErrorText,
  causeLabel,
  durationText,
  type EscalationMetrics,
  type EscalationsApi,
  type PolicyKind,
  type SiteSystemAlert,
} from './alertActionsModel';
import { BTN_ICON, BTN_PRIMARY_SM, BTN_SECONDARY_SM } from '../common/ui';

export interface EscalationsPanelProps {
  api: EscalationsApi;
  kind: PolicyKind;
  /** alerts.edit — acknowledge / resolve system problems. */
  canAct: boolean;
  /** Above everything — the kind switch. */
  header?: ReactNode;
  eventTypeLabel?: (t: string) => string;
}

const RANGES = [
  { value: '1', label: 'Last 24 hours', short: 'the last 24 hours' },
  { value: '7', label: '7 days', short: 'the last 7 days' },
  { value: '30', label: '30 days', short: 'the last 30 days' },
  { value: '90', label: '90 days', short: 'the last 90 days' },
] as const;

const pct = (n: number, of: number) => (of ? `${Math.round((n / of) * 100)}%` : '—');
/** "20 min" / "2 h 5 min" — how long something has been waiting (whole minutes). */
const ago = (iso: string) => durationText(Math.max(60, Math.round((Date.now() - new Date(iso).getTime()) / 60000) * 60));
const dayText = (day: string) => new Date(`${day}T00:00:00`).toLocaleDateString([], { day: 'numeric', month: 'short' });
const inMin = (iso: string | null) => {
  if (!iso) return null;
  const m = Math.round((new Date(iso).getTime() - Date.now()) / 60000);
  return m <= 0 ? 'any moment' : `in ${m} min`;
};
const kindLabel = (k: string) => SYSTEM_ALERT_KINDS.find((x) => x.value === k)?.label || k;
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function Number_({ label, value, sub, tone }: { label: string; value: ReactNode; sub?: ReactNode; tone?: 'danger' }) {
  return (
    <div className="rounded-xl border border-border bg-card px-3 py-2.5">
      <p className="text-[12px] text-muted-foreground">{label}</p>
      <p className={`mt-0.5 text-[24px] font-semibold tabular-nums ${tone === 'danger' ? 'text-destructive' : 'text-foreground'}`}>{value}</p>
      {sub && <p className="text-[12px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

/** Alerts per day — one series, so no legend; each bar's tooltip carries the day's detail. */
function PerDay({ days }: { days: NonNullable<EscalationMetrics['response']>['by_day'] }) {
  const max = Math.max(1, ...days.map((d) => d.total));
  if (!days.length) return <p className="text-[12px] text-muted-foreground">No alerts in this period.</p>;
  return (
    <div>
      <div className="flex h-28 items-end gap-0.5" role="img" aria-label="Alerts per day">
        {days.map((d) => (
          <div key={d.day} className="group relative flex h-full min-w-[6px] flex-1 items-end">
            <div className="w-full rounded-t bg-brand" style={{ height: `${Math.max(2, (d.total / max) * 100)}%` }} />
            <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded-lg border border-border bg-card px-2 py-1 text-[11px] text-foreground shadow group-hover:block">
              <p className="font-semibold">{dayText(d.day)}</p>
              <p>{plural(d.total, 'alert')} · {d.responded} answered</p>
              <p>{d.escalated} passed on · {d.ended} never answered</p>
              <p>usually answered in {durationText(d.tta_avg)}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[11px] tabular-nums text-muted-foreground">
        <span>{dayText(days[0].day)}</span>
        <span>busiest day: {max}</span>
        <span>{dayText(days[days.length - 1].day)}</span>
      </div>
    </div>
  );
}

function Table({ head, rows, empty }: { head: string[]; rows: ReactNode[][]; empty: string }) {
  if (!rows.length) return <p className="text-[12px] text-muted-foreground">{empty}</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]">
        <thead>
          <tr className="border-b border-border text-left text-[12px] text-muted-foreground">
            {head.map((h, i) => <th key={h} className={`py-1.5 pr-3 font-medium ${i > 0 ? 'text-right' : ''}`}>{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-border last:border-0">
              {r.map((c, j) => <td key={j} className={`py-1.5 pr-3 text-foreground ${j > 0 ? 'text-right tabular-nums' : ''}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Box({ title, hint, children, right }: { title: string; hint?: string; children: ReactNode; right?: ReactNode }) {
  return (
    <section className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="mb-2 flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <h3 className="text-[14px] font-semibold text-foreground">{title}</h3>
          {hint && <p className="text-[12px] text-muted-foreground">{hint}</p>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

/** The site's system problems — Acknowledge / Resolve here (also on the cloud's System Alerts). */
function SystemProblems({ api, canAct }: { api: EscalationsApi; canAct: boolean }) {
  const [rows, setRows] = useState<SiteSystemAlert[] | null>(null);
  const [all, setAll] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  // "Mark fixed" opens the short report under the row.
  const [fixing, setFixing] = useState<string | null>(null);
  const [report, setReport] = useState<ResolveReportValue>({ cause: '', note: '' });
  const load = useCallback(() => {
    api.systemAlerts(all ? 'all' : 'live')
      .then((r) => { setRows(r); setError(''); })
      .catch((err) => setError(alertActionsErrorText(err, 'Could not read the system problems')));
  }, [api, all]);
  useEffect(() => { load(); }, [load]);

  const act = async (row: SiteSystemAlert, action: 'acknowledge' | 'resolve') => {
    setBusy(row.id);
    try {
      const next = await api.setSystemAlertStatus(row.id, action, action === 'resolve'
        ? { ...(report.cause ? { cause: report.cause } : {}), ...(report.note.trim() ? { note: report.note.trim() } : {}) }
        : undefined);
      if (action === 'resolve') { setFixing(null); setReport({ cause: '', note: '' }); }
      setRows((list) => (list || []).map((r) => (r.id === row.id ? next : r)).filter((r) => all || r.status !== 'resolved'));
    } catch (err) {
      setError(alertActionsErrorText(err, `Could not ${action} it`));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box
      title="System problems"
      hint="Acknowledge one to say someone is on it — that stops telling more people. Resolve it when it is fixed. CPU, memory, disk, a service running again and a camera streaming again resolve themselves."
      right={(
        <label className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} className="accent-brand" /> Show fixed ones too
        </label>
      )}
    >
      {error && <Callout tone="danger">{error}</Callout>}
      {rows === null ? <p className="text-[13px] text-muted-foreground">Loading…</p> : rows.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">{all ? 'No system problems yet.' : 'All clear — no open system problems.'}</p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map((r) => (
            <li key={r.id} className="flex flex-wrap items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium text-foreground">
                  {r.title || kindLabel(r.kind)}
                  <StatusPill tone={r.status === 'open' ? 'danger' : r.status === 'acknowledged' ? 'warning' : 'success'}>
                    {r.status === 'open' ? 'Waiting for someone' : r.status === 'acknowledged' ? 'Someone is on it' : 'Fixed'}
                  </StatusPill>
                  {(r.escalation_level || 0) > 1 && (
                    <StatusPill tone={r.escalation_state === 'ended' ? 'danger' : 'warning'}>Passed on · step {r.escalation_level}</StatusPill>
                  )}
                  {r.suppressed && r.status !== 'resolved' && (
                    <span title={r.suppressed === 'parent' ? 'The service that serves these cameras is down — its own alert covers them. Sent if they are still down once it is back.' : 'A pause is in force — sent if still open when it ends.'}>
                      <StatusPill tone="neutral">{r.suppressed === 'parent' ? 'Held back — its service is down' : 'Paused — nobody told'}</StatusPill>
                    </span>
                  )}
                </p>
                <p className="truncate text-[12px] text-muted-foreground" title={r.message || undefined}>
                  {[r.machine || r.service, r.occurrences > 1 ? `happened ${r.occurrences} times` : null, `started ${ago(r.first_at)} ago`].filter(Boolean).join(' · ')}
                </p>
                {(r.acknowledged_by || r.resolved_by) && (
                  <p className="text-[12px] text-muted-foreground">
                    {r.resolved_how === 'auto' ? `Recovered by itself — ${r.resolved_by}` : r.resolved_by ? `Fixed — ${r.resolved_by}` : `On it — ${r.acknowledged_by}`}
                    {r.cause && ` · cause: ${causeLabel(r.cause)}`}
                    {r.resolution_note && ` · ${r.resolution_note}`}
                  </p>
                )}
              </div>
              {canAct && r.status !== 'resolved' && (
                <div className="flex shrink-0 gap-1.5">
                  {r.status === 'open' && (
                    <button type="button" disabled={busy === r.id} onClick={() => act(r, 'acknowledge')}
                      className={BTN_PRIMARY_SM}>
                      <CheckIcon className="h-4 w-4" /> I'm on it
                    </button>
                  )}
                  <button type="button" disabled={busy === r.id} onClick={() => { setFixing(fixing === r.id ? null : r.id); setReport({ cause: '', note: '' }); }}
                    className={BTN_SECONDARY_SM}>
                    Mark fixed
                  </button>
                </div>
              )}
              {fixing === r.id && (
                <div className="basis-full space-y-2 rounded-lg border border-border bg-muted px-3 py-2.5">
                  <ResolveReport value={report} onChange={setReport} />
                  <div className="flex justify-end gap-1.5">
                    <button type="button" onClick={() => setFixing(null)} className={BTN_SECONDARY_SM}>Cancel</button>
                    <button type="button" disabled={busy === r.id} onClick={() => act(r, 'resolve')}
                      className={BTN_PRIMARY_SM}>
                      <CheckIcon className="h-4 w-4" /> Mark fixed
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Box>
  );
}

export default function EscalationsPanel({ api, kind, canAct, header, eventTypeLabel = (t) => t }: EscalationsPanelProps) {
  const [days, setDays] = useState<string>('7');
  const [data, setData] = useState<EscalationMetrics | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await api.metrics(Number(days)));
      setError('');
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not read the numbers'));
    } finally {
      setLoading(false);
    }
  }, [api, days]);
  useEffect(() => { load(); }, [load]);

  const open = useMemo(() => (data?.open || []).filter((o) => o.kind === kind), [data, kind]);
  const policies = useMemo(() => (data?.policies || []).filter((p) => p.kind === kind), [data, kind]);
  const period = RANGES.find((x) => x.value === days)?.short || 'this period';
  const r = data?.response;
  const s = data?.system;

  const summary = kind === 'ai'
    ? (r ? (r.total === 0 ? <>No AI alerts in {period}.</> : (
      <>
        In {period} there {r.total === 1 ? 'was' : 'were'} <b>{plural(r.total, 'alert')}</b>.{' '}
        <b>{r.responded}</b> ({pct(r.responded, r.total)}) {r.responded === 1 ? 'was' : 'were'} answered
        {r.tta.avg !== null && <> — usually within <b>{durationText(r.tta.avg)}</b>, and 9 in 10 within <b>{durationText(r.tta.p90)}</b></>}.{' '}
        {r.escalated > 0 ? <><b>{r.escalated}</b> had to be passed on to more people</> : <>None had to be passed on to more people</>}
        {r.ended > 0 ? <>, and <b className="text-destructive">{r.ended} {r.ended === 1 ? 'was' : 'were'} never answered</b> even after everyone was told.</> : '.'}
        {r.open > 0 && <> <b>{r.open}</b> {r.open === 1 ? 'is' : 'are'} still waiting.</>}
      </>
    )) : data?.response_error ? <>{data.response_error}</> : null)
    : (s ? (s.total === 0 ? <>No system problems in {period}.</> : (
      <>
        In {period} there {s.total === 1 ? 'was' : 'were'} <b>{plural(s.total, 'system problem')}</b>.{' '}
        <b>{s.acknowledged}</b> ({pct(s.acknowledged, s.total)}) got a response
        {s.tta.avg !== null && <> — usually within <b>{durationText(s.tta.avg)}</b></>}; <b>{s.resolved}</b> {s.resolved === 1 ? 'is' : 'are'} fixed.{' '}
        {s.escalated > 0 ? <><b>{s.escalated}</b> had to be passed on to more people</> : <>None had to be passed on</>}
        {s.ended > 0 ? <>, and <b className="text-destructive">{s.ended} never got a response</b>.</> : '.'}
      </>
    )) : null);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-0 flex-1">{header}</div>
        <AASegmented value={days} onChange={setDays} options={RANGES.map((x) => ({ value: x.value, label: x.label }))} />
        <button type="button" onClick={load} className={BTN_ICON} title="Reload" aria-label="Reload">
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>
      {error && <Callout tone="danger">{error}</Callout>}

      {summary && <p className="rounded-xl border border-border bg-muted px-4 py-3 text-[14px] leading-relaxed text-foreground">{summary}</p>}

      {kind === 'system' && <SystemProblems api={api} canAct={canAct} />}

      <Box
        title={open.length ? `Being passed on now (${open.length})` : 'Being passed on now'}
        hint={kind === 'ai'
          ? 'Alerts nobody has acknowledged yet. Acknowledge one on the alert board to stop it.'
          : 'System problems nobody has acknowledged yet.'}
      >
        {open.length === 0 ? <p className="text-[13px] text-muted-foreground">Nothing is waiting.</p> : (
          <ul className="divide-y divide-border">
            {open.map((o) => (
              <li key={`${o.alert_id}:${o.policy}`} className="py-2 text-[13px]">
                <p className="text-foreground"><b>{o.title}</b>{o.source ? <span className="text-muted-foreground"> · {o.source}</span> : null}</p>
                <p className="text-[12px] text-muted-foreground">
                  Waiting {ago(o.started_at)} · {o.level > 1 ? `step ${o.level} has been told` : 'first people told'}
                  {o.reminders ? `, reminded ${o.reminders}×` : ''}
                  {inMin(o.next_at) ? ` · next step ${inMin(o.next_at)}` : ''} · {o.policy}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Box>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        {kind === 'ai' ? (
          <>
            <Number_ label="Alerts" value={r ? r.total : '—'} />
            <Number_ label="Answered" value={r ? pct(r.responded, r.total) : '—'} sub={r ? `${r.responded} of ${r.total}` : undefined} />
            <Number_ label="Usual time to answer" value={r ? durationText(r.tta.avg) : '—'} sub={r && r.tta.p90 !== null ? `9 in 10 within ${durationText(r.tta.p90)}` : undefined} />
            <Number_ label="Never answered" value={r ? r.ended : '—'} sub="even after everyone was told" tone={r && r.ended ? 'danger' : undefined} />
          </>
        ) : (
          <>
            <Number_ label="System problems" value={s ? s.total : '—'} sub={s ? `${s.live} not fixed yet` : undefined} />
            <Number_ label="Got a response" value={s ? pct(s.acknowledged, s.total) : '—'} sub={s ? `${s.acknowledged} of ${s.total}` : undefined} />
            <Number_ label="Usual time to respond" value={s ? durationText(s.tta.avg) : '—'} sub={s && s.tta.p90 !== null ? `9 in 10 within ${durationText(s.tta.p90)}` : undefined} />
            <Number_ label="Never got a response" value={s ? s.ended : '—'} sub="even after everyone was told" tone={s && s.ended ? 'danger' : undefined} />
          </>
        )}
      </div>

      <div className="rounded-xl border border-border">
        <button type="button" onClick={() => setDetail((v) => !v)} className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-[13px] font-medium text-foreground">
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${detail ? '' : '-rotate-90'}`} />
          More detail
          <span className="font-normal text-muted-foreground">— {kind === 'ai' ? 'per day, per notification, who answered, per alert type' : 'per notification, per kind of problem'}</span>
        </button>
        {detail && (
          <div className="grid grid-cols-1 gap-3 border-t border-border p-3 xl:grid-cols-2">
            {kind === 'ai' && (
              <Box title="Alerts per day" hint="Point at a day to see its numbers.">
                <PerDay days={r?.by_day || []} />
              </Box>
            )}
            <Box title="Per notification" hint="Only notifications that tell more people when nobody answers.">
              <Table
                head={['Notification', 'Alerts', 'Answered', 'After passing on', 'Never answered']}
                empty="None of these caught an alert in this period."
                rows={policies.map((p) => [p.name, p.started, p.acknowledged, p.acknowledged_after_escalation, p.ended])}
              />
            </Box>
            {kind === 'ai' ? (
              <>
                <Box title="Who answered" hint="Who answered each alert first, and how quickly.">
                  <Table
                    head={['Person', 'Alerts answered', 'Usually within', 'Only after passing on']}
                    empty="Nobody answered an alert in this period."
                    rows={(r?.responders || []).map((x) => [x.name, x.responses, durationText(x.avg_secs), x.after_escalation])}
                  />
                </Box>
                <Box title="Per alert type">
                  <Table
                    head={['Alert type', 'Alerts', 'Passed on', 'Never answered', 'Usually answered within']}
                    empty="No alerts in this period."
                    rows={(r?.by_type || []).map((x) => [eventTypeLabel(x.event_type), x.total, x.escalated, x.ended, durationText(x.tta_avg)])}
                  />
                </Box>
                {r && <p className="text-[12px] text-muted-foreground xl:col-span-2">Time to fix (resolve): usually {durationText(r.ttr.avg)}, 9 in 10 within {durationText(r.ttr.p90)} · {r.resolved} resolved.</p>}
                {r && (r.by_cause || []).length > 0 && (
                  <p className="text-[12px] text-muted-foreground xl:col-span-2">
                    What caused them (as picked when resolving): {(r.by_cause || []).map((c) => `${causeLabel(c.cause)} ${c.total}`).join(' · ')}.
                  </p>
                )}
                {r && (r.repeats?.events || 0) > 0 && (
                  <p className="text-[12px] text-muted-foreground xl:col-span-2">
                    {r.repeats!.events} repeats of {plural(r.repeats!.alerts, 'alert')} were counted on the alert instead of raising new ones (each rule's “Don't repeat it for” time).
                  </p>
                )}
              </>
            ) : (
              <Box title="Per kind of problem">
                <Table
                  head={['Problem', 'Times', 'Got a response', 'Passed on']}
                  empty="No system problems in this period."
                  rows={(s?.by_kind || []).map((x) => [kindLabel(x.kind), x.total, x.acknowledged, x.escalated])}
                />
              </Box>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
